import { css } from "styled-components";

type Breakpoint = "desktop" | "tablet" | "mobile";
type ResponsiveSize = { desktop: string; tablet: string; mobile: string };

export const typography = {
  fontFamily: {
    primary: "'Motiva Sans Light', sans-serif",
    bold: "'Motiva Sans Bold', serif",
  },
  fontSize: {
    h1: { desktop: "56px", tablet: "47px", mobile: "32px" },
    h2: { desktop: "48px", tablet: "40px", mobile: "28px" },
    h3: { desktop: "40px", tablet: "32px", mobile: "24px" },
    h4: { desktop: "32px", tablet: "28px", mobile: "22px" },
    h5: { desktop: "24px", tablet: "20px", mobile: "18px" },
    h6: { desktop: "20px", tablet: "18px", mobile: "16px" },
    body: {
      large: { desktop: "21px", tablet: "19px", mobile: "17px" },
      medium: { desktop: "18px", tablet: "16px", mobile: "15px" },
      small: { desktop: "16px", tablet: "14px", mobile: "13px" },
    },
    caption: { desktop: "14px", tablet: "13px", mobile: "12px" },
    small: { desktop: "12px", tablet: "11px", mobile: "10px" },
  },
  lineHeight: {
    tight: "1.1",
    normal: "1.2",
    relaxed: "1.4",
    loose: "1.6",
  },
  fontWeight: {
    light: "300",
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
  },
} as const;

export const getResponsiveFontSize = (
  size: keyof typeof typography.fontSize | ResponsiveSize,
  breakpoint: Breakpoint = "desktop"
): string => {
  if (typeof size === "object") {
    return size[breakpoint];
  }

  const fontSize = typography.fontSize[size];
  return typeof fontSize === "string"
    ? fontSize
    : (fontSize as ResponsiveSize)[breakpoint];
};

export const responsiveFontSize = (
  size: keyof typeof typography.fontSize | ResponsiveSize
) => css`
  font-size: ${getResponsiveFontSize(size, "desktop")};

    @media (max-width: 890px) {
    font-size: ${getResponsiveFontSize(size, "mobile")};
  }
`;

export const textStyles = {
  heading: (size: "h1" | "h2" | "h3" | "h4" | "h5" | "h6") => css`
    font-family: ${typography.fontFamily.bold};
    font-weight: ${typography.fontWeight.semibold};
    line-height: ${typography.lineHeight.tight};
    ${responsiveFontSize(size)}
  `,
  body: (variant: "large" | "medium" | "small") => css`
    font-family: ${typography.fontFamily.primary};
    font-weight: ${typography.fontWeight.normal};
    line-height: ${typography.lineHeight.relaxed};
    ${responsiveFontSize(typography.fontSize.body[variant])}
  `,
  caption: css`
    font-family: ${typography.fontFamily.primary};
    font-weight: ${typography.fontWeight.normal};
    line-height: ${typography.lineHeight.normal};
    ${responsiveFontSize(typography.fontSize.caption)}
  `,
  small: css`
    font-family: ${typography.fontFamily.primary};
    font-weight: ${typography.fontWeight.normal};
    line-height: ${typography.lineHeight.normal};
    ${responsiveFontSize(typography.fontSize.small)}
  `,
};
